import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { SavedCard } from '@app/shared/models/subscription-renewal.model';
import { SubscriptionInfo, SubscriptionStatus } from '@app/shared/models/subscription-info.model';

export const SUBSCRIPTION_STATUS_STORAGE_KEY = 'subscriptionStatus';
const STORAGE_KEY = SUBSCRIPTION_STATUS_STORAGE_KEY;
const KNOWN: SubscriptionStatus[] = ['active', 'expiringSoon', 'expired'];

/** Holds the signed-in company's latest subscription status so layout components can react to it. */
@Injectable({ providedIn: 'root' })
export class SubscriptionStateService {
  private readonly _status$: BehaviorSubject<SubscriptionStatus | null>;

  private readonly _savedCardUpdated$ = new Subject<SavedCard>();

  constructor(private _storage: LocalStorageService) {
    const saved = this._storage.getValueByKey(STORAGE_KEY) as SubscriptionStatus;
    this._status$ = new BehaviorSubject<SubscriptionStatus | null>(KNOWN.indexOf(saved) >= 0 ? saved : null);
  }

  get status$(): Observable<SubscriptionStatus | null> {
    return this._status$.asObservable();
  }

  /** The new masked card each time the owner replaces the saved card, so every screen showing it can update. */
  get savedCardUpdated$(): Observable<SavedCard> {
    return this._savedCardUpdated$.asObservable();
  }

  get isExpired(): boolean {
    return this._status$.value === 'expired';
  }

  /** Stores the status from a profile response; a missing block clears it. */
  update(info: SubscriptionInfo | null): void {
    if (info && KNOWN.indexOf(info.status) >= 0) {
      this._storage.saveValueByKey(STORAGE_KEY, info.status);
      this._status$.next(info.status);
    } else {
      this.clear();
    }
  }

  notifySavedCardUpdated(card: SavedCard): void {
    this._savedCardUpdated$.next(card);
  }

  clear(): void {
    this._storage.deleteValueByKey(STORAGE_KEY);
    this._status$.next(null);
  }
}
